import React, { useId } from 'react'
import styled from 'styled-components'
import { mergeProps, useRadio } from 'react-aria'
import invariant from 'tiny-invariant'
import { typeValidator } from '@chilipiper/utils'
import { Box, Flex, Grid, ScreenReaderOnly, Text } from '../../../../core-components'
import { useFocusVisible } from '../../../hooks'
import { OmitInternalProperties } from '../../../types'
import { ThemeColors, ThemeShadows } from '../../../../theme'
import { useRadioContext } from '../RadioContext'
import { EndContent, EndContentProvider, StartContent, StartContentProvider } from '../../../data'
import { Radio } from './radio/Radio'

type RadioItemProps = {
  INTERNAL_isFocusVisible?: boolean
  children?: React.ReactNode
  'data-id'?: string
  'data-test-id'?: string
  description?: string
  endContent?: React.ReactNode
  isDisabled?: boolean
  label: string
  startContent?: React.ReactNode
  value: string
}

export const RadioItemInternal = ({
  children,
  value,
  startContent,
  endContent,
  label,
  isDisabled = false,
  description,
  'data-id': dataId,
  'data-test-id': dataTestId,
  INTERNAL_isFocusVisible,
}: RadioItemProps) => {
  const nestedContentId = useId()
  const radioId = useId()
  invariant(
    startContent === undefined || typeValidator(RadioStartContent)(startContent),
    'startContent must be a Radio.StartContent component or undefined'
  )
  invariant(
    endContent === undefined || typeValidator(RadioEndContent)(endContent),
    'endContent must be a Radio.EndContent component or undefined'
  )
  const { name, state } = useRadioContext()
  const ref = React.useRef(null)
  const { inputProps, isSelected } = useRadio({ value, isDisabled, children: label }, state, ref)
  const { isFocusVisible, focusProps } = useFocusVisible(INTERNAL_isFocusVisible)
  return (
    <RadioContainer
      isSelected={isSelected}
      isDisabled={isDisabled}
      boxShadow={getContainerShadow(isFocusVisible, isSelected)}
      bg={getContainerBg(isFocusVisible, isSelected, isDisabled)}
      data-test-id={`${dataTestId}-container`}
      position={
        'relative' /* To handle https://filip-wachowiak.vercel.app/blog/default-absolute-positioning-in-a-scroll-container */
      }
    >
      <ScreenReaderOnly>
        <input
          {...mergeProps(inputProps, focusProps)}
          aria-controls={children ? nestedContentId : undefined}
          id={radioId}
          ref={ref}
          type='radio'
          name={name}
          value={value}
          disabled={isDisabled}
        />
      </ScreenReaderOnly>
      <StyledGrid
        as='label'
        htmlFor={radioId}
        cursor={isDisabled ? 'default' : 'pointer'}
        gridTemplateColumns='min-content 1fr'
        px={6}
        py={4}
        data-test-id={dataTestId}
        data-id={dataId}
      >
        <Radio isSelected={isSelected} isDisabled={isDisabled} isFocused={isFocusVisible} />
        <Content
          startContent={startContent}
          isDisabled={isDisabled}
          isSelected={isSelected}
          label={label}
          endContent={endContent}
        />
        {description && (
          <StyledText
            textStyle='body-multiline-tight'
            color={isDisabled ? 'fg/disabled' : 'fg/moderate'}
          >
            {description}
          </StyledText>
        )}
      </StyledGrid>
      {isSelected && children && (
        <Box mx={6} pt={5} pb={6} boxShadow='border/row-top' id={nestedContentId}>
          {children}
        </Box>
      )}
    </RadioContainer>
  )
}

type PublicRadioItemProps = OmitInternalProperties<RadioItemProps>

export const RadioItemPublic = RadioItemInternal as unknown as React.FC<PublicRadioItemProps>
export const RadioStartContent = (props: React.ComponentProps<typeof StartContent>) => (
  <StartContent {...props} />
)
export const RadioEndContent = (props: React.ComponentProps<typeof EndContent>) => (
  <EndContent {...props} />
)

const RadioContainer = styled(Box)<{ isSelected: boolean; isDisabled: boolean }>`
  :hover {
    background: ${({ isSelected, isDisabled, theme }) =>
      !isSelected && !isDisabled && theme.colors['bg/list-item-hovered']};
  }
`
const StyledGrid = styled(Grid)`
  gap: 8px 16px;
`
const StyledText = styled(Text)`
  grid-column: 2;
  grid-row: 2;
`

interface ContentProps {
  endContent?: React.ReactNode
  isDisabled?: undefined | boolean
  isSelected: boolean
  label: string
  startContent?: React.ReactNode
}

const Content = ({ isDisabled, endContent, label, isSelected, startContent }: ContentProps) => (
  <Flex alignItems='center' gap={2}>
    {startContent && (
      <StartContentProvider value={{ iconColor: getLabelColor(isDisabled) }}>
        {startContent}
      </StartContentProvider>
    )}
    <Text
      flexGrow={1}
      textStyle={isSelected ? 'item-selected' : 'item-unselected'}
      color={getLabelColor(isDisabled)}
    >
      {label}
    </Text>
    {endContent && <EndContentProvider value={{}}>{endContent}</EndContentProvider>}
  </Flex>
)

const getLabelColor = (isDisabled?: boolean): ThemeColors => {
  if (isDisabled) {
    return 'fg/disabled'
  }
  return 'fg/major'
}

const getContainerShadow = (isFocused: boolean, isSelected: boolean): ThemeShadows => {
  if (isFocused) {
    return 'border/focus-indicator'
  }
  if (isSelected) {
    return 'border/list-item-selected'
  }
  return 'border/row'
}

const getContainerBg = (
  isFocused: boolean,
  isSelected: boolean,
  isDisabled?: boolean
): ThemeColors => {
  if (isDisabled) {
    return 'bg/disabled'
  }
  if (isFocused) {
    return 'bg/list-item-hovered'
  }
  if (isSelected) {
    return 'bg/list-item-selected'
  }
  return 'bg/list-item-default'
}
